/**
 * PATH: packages/email-hub/src/feature/reader-header/runtime/telemetry.ts
 * FILE_ID: EMAILHUB-READERHEADER-RUNTIME-TELEMETRY-001
 * PURPOSE: Emit structured reader-header action lifecycle events
 *   (invoked / optimistic_applied / server_ok / server_error / rollback)
 *   with zero message content — ids, kinds, timings, and buckets only.
 * ROLE IN ARCHITECTURE: Observability leaf under the action runner; routes
 *   through the existing window.__QT_OBS__ ring buffer (API-OBS-SEAM-001).
 * MAIN EXPORTS: emitReaderTelemetry, setReaderTelemetrySink, cardinalityBucket.
 * NON-RESPONSIBILITIES: No sampling, no export/ingest — the seam sink owns that.
 * NOTES FOR FUTURE AI: NEVER add subject/body/address/payload text fields.
 *   The privacy test pins the allowed key set; extend it deliberately.
 */
import type {
  ReaderErrorClass,
  ReaderHeaderSurface,
  ReaderHeaderZone,
  ReaderInputMethod,
} from '../model/types'

export type ReaderTelemetryPhase =
  | 'action_invoked'
  | 'optimistic_applied'
  | 'server_ok'
  | 'server_error'
  | 'rollback'

export interface ReaderTelemetryEvent {
  /** e.g. `reader_header.action_invoked` */
  event: `reader_header.${ReaderTelemetryPhase}`
  /** Correlation id — the same value stamped as X-Correlation-ID. */
  actionId: string
  /** Registry action id (e.g. 'archive', 'accept_decision'). */
  actionKind: string
  zone: ReaderHeaderZone
  surface: ReaderHeaderSurface
  inputMethod: ReaderInputMethod
  durationMs?: number
  errorClass?: ReaderErrorClass
  /** Bucketed target count — never the raw content. */
  cardinalityBucket: '1' | '2-10' | '11-50' | '50+'
  ts: number
}

/** Pure: bucket a target count so telemetry never carries exact volumes. */
export function cardinalityBucket(
  count: number,
): ReaderTelemetryEvent['cardinalityBucket'] {
  if (count <= 1) return '1'
  if (count <= 10) return '2-10'
  if (count <= 50) return '11-50'
  return '50+'
}

type TelemetrySink = (event: ReaderTelemetryEvent) => void
let sink: TelemetrySink | null = null

/** Test/ingest override; pass null to restore the default seam route. */
export function setReaderTelemetrySink(next: TelemetrySink | null): void {
  sink = next
}

interface ObsRingEvent {
  ts: number
  kind: string
  key: unknown
  actionId?: string
  durationMs?: number
  payload?: unknown
  error?: string
}

/**
 * Emit one lifecycle event.
 *
 * Route: pluggable sink (tests/ingest) → window.__QT_OBS__ ring buffer when
 * present (same buffer the TanStack seam tap fills) → console.debug fallback.
 * Side effects: appends to the ring buffer. Failure modes: swallows sink
 * errors — telemetry must never break the action it observes.
 */
export function emitReaderTelemetry(
  event: Omit<ReaderTelemetryEvent, 'ts'>,
): void {
  const full: ReaderTelemetryEvent = { ...event, ts: Date.now() }
  try {
    if (sink) {
      sink(full)
      return
    }
    const obs =
      typeof window !== 'undefined'
        ? (window as { __QT_OBS__?: { events?: ObsRingEvent[] } }).__QT_OBS__
        : undefined
    if (obs?.events) {
      obs.events.push({
        ts: full.ts,
        kind: full.event,
        key: [full.actionKind, full.surface, full.zone],
        actionId: full.actionId,
        durationMs: full.durationMs,
        error: full.errorClass,
        payload: {
          inputMethod: full.inputMethod,
          cardinalityBucket: full.cardinalityBucket,
        },
      })
      if (obs.events.length > 500) obs.events.splice(0, obs.events.length - 500)
      return
    }
    // eslint-disable-next-line no-console -- last-resort sink when neither a test sink nor the seam ring buffer exists (SSR/unit envs); debug level keeps prod consoles quiet.
    console.debug('[reader-header]', full.event, full)
  } catch {
    // Observability must never take down the observed action.
  }
}
